import _ from 'lodash';
import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  QUICK_ADJUSTMENT_ACTIONS,
  QUICK_ADJUSTMENT_TRIGGERS,
} from 'v2/common/constants/index';
import ModalComponent from 'v3/common/components/ModalComponent';
import ErrorMesssage from 'v2/common/components/ErrorMessage/index';
import { quickAdjustmentPropTypes } from 'common/propTypes';
import { useQuickAdjustments } from 'v2/modules/withRunForm/hooks/useQuickAdjustments/index';
import QuickAdjustmentsForm, { BLANK_QUICK_ADJUSTMENT } from './QuickAdjustmentForm';
import { isCaseBasedAction } from './Utils';
import { useSelector } from 'react-redux';
import { getSelectorBasedStoreValue } from 'v2/modules/withRunForm/redux/selector';
import { getQuantModes } from 'v2/modules/withRunForm/configs';

const propTypes = {
  intentId: PropTypes.string.isRequired,
  quickAdjustments: PropTypes.arrayOf(quickAdjustmentPropTypes),
  onUpdate: PropTypes.func.isRequired,
};

const defaultProps = {
  quickAdjustments: [],
};

const renderQuickAdjustmentText = (quickAdjustment, index) => {
  const {
    trigger, action, targets, delayBy
  } = quickAdjustment;
  const triggerItem = _.find(QUICK_ADJUSTMENT_TRIGGERS, { value: trigger });
  const actionItem = _.find(QUICK_ADJUSTMENT_ACTIONS, { value: action });
  const renderLabel = isCaseBasedAction(action) ? 'Case' : 'Leg';

  const badgeClassNames = classNames(
    'quick-adjustment-badge badge badge-light align-middle'
  );
  const triggerClassNames = classNames('trigger', {
    'text-success': triggerItem === QUICK_ADJUSTMENT_TRIGGERS.ExitedInProfit,
    'text-danger': triggerItem === QUICK_ADJUSTMENT_TRIGGERS.ExitedInLoss,
  });
  const targetLabels = targets.map((target) => `${isCaseBasedAction(action) ? _.toNumber(target) + 1 : target}`).join(' | ');
  return (
    <div className={badgeClassNames} key={index}>
      <span className={triggerClassNames}>
        On&nbsp;
        {triggerItem?.shortLabel}
        ,
      </span>
      <span className="action">{actionItem?.shortLabel}</span>
      <span className="targets">
        {_.capitalize(renderLabel)}&nbsp;
        {targetLabels}
      </span>
      {_.isNumber(delayBy) && (
        <div className="delay-by">
          after&nbsp;
          {delayBy}
          &nbsp;secs
        </div>
      )}
    </div>
  );
};

const renderQuickAdjustmentsText = (quickAdjustments) => {
  if (!quickAdjustments || quickAdjustments.length === 0) {
    return (
      <button
        className="btn btn-outline-secondary   w-100 track tx-14"
        type="button"
      >
        <i className="fa-regular fa-plus-large  me-1 tx-12"></i>
        Quick Adjustments
      </button>
    );
  }

  return [
    <button type="button" className="edit-btn" key={-1}>
      Edit
    </button>
  ].concat(
    quickAdjustments.map(renderQuickAdjustmentText)
  );
};

const QuickAdjustments = ({ intentId, quickAdjustments, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const { fieldId, error } = useQuickAdjustments(intentId, quickAdjustments);
  const [localQuickAdjustments, setLocalQuickAdjustments] = useState(quickAdjustments);
  const mode = useSelector((state) => getSelectorBasedStoreValue(state, 'quantConfig.uiConfigs.mode'));
  const isSimpleMode = mode === getQuantModes.simple.value;

  useEffect(
    () => setLocalQuickAdjustments(quickAdjustments),
    [quickAdjustments]
  );

  useEffect(() => {
    if (showModal && _.isEmpty(quickAdjustments)) {
      setLocalQuickAdjustments([BLANK_QUICK_ADJUSTMENT]);
    }
  }, [quickAdjustments, setLocalQuickAdjustments, showModal]);

  const onModalSave = useCallback(() => {
    setShowModal(false);
    onUpdate(localQuickAdjustments);
  }, [localQuickAdjustments, onUpdate]);

  const onModalClose = useCallback(() => {
    setShowModal(false);
    setLocalQuickAdjustments(quickAdjustments);
  },[quickAdjustments]);

  const shouldConfirmClose = useCallback(() => {
    return !_.chain(localQuickAdjustments)
      .filter(BLANK_QUICK_ADJUSTMENT)
      .isEqual(quickAdjustments)
      .value();
  }, [quickAdjustments, localQuickAdjustments]);

  const textContainerClassNames = classNames('text-container', `total-items-${quickAdjustments.length}`);

  // TODD:Command this one for we dont have isSimpleMode check, we enable all user having multiple cases added.

  // useEffect(() => {
  //   if (isSimpleMode) {
  //       const newQuickAdjustments = quickAdjustments.map((quickAdjustment) => {
  //       if (isCaseBasedAction(quickAdjustment.action)) {
  //         return {
  //           ...quickAdjustment,
  //           targets: []
  //         };
  //       }
  //       return quickAdjustment;
  //     });
  //     setLocalQuickAdjustments(newQuickAdjustments);
  //     }
  // }, [isSimpleMode, quickAdjustments]);

  
  return (
    <div className="transaction_details-items quick-adjustment" id={fieldId}>
      <div className={textContainerClassNames} onClick={() => setShowModal(!showModal)}>
        <ErrorMesssage message={error.message} isPremiumError={error.isPremiumError}>
          {renderQuickAdjustmentsText(localQuickAdjustments)}
        </ErrorMesssage>
      </div>
      <ModalComponent
        title="Leg's Quick Adjustments"
        size="xl"
        shouldShow={showModal}
        shouldConfirmClose={shouldConfirmClose}
        onSave={onModalSave}
        onClose={onModalClose}
        onCancel={onModalClose}
      >
        <QuickAdjustmentsForm
          intentId={intentId}
          quickAdjustments={localQuickAdjustments}
          onUpdate={setLocalQuickAdjustments}
        />
      </ModalComponent>
    </div>
  );
};

QuickAdjustments.propTypes = propTypes;
QuickAdjustments.defaultProps = defaultProps;

export default QuickAdjustments;
